import { getEtMarketDateKey, getMarketSession, isUsEquityTradingDay } from '../marketSession.js'
import { millisecondsUntilDailyClose } from '../marketRegime.js'

// The dashboard uses the same normalized daily day keys as the chart adapter.
export function validDailyBar(bar) {
  const key = String(bar?.time ?? '')
  if (!/^\d{8}$/.test(key)) return false
  const date = new Date(`${key.slice(0, 4)}-${key.slice(4, 6)}-${key.slice(6, 8)}T00:00:00Z`)
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10).replaceAll('-', '') !== key) return false
  if (['open', 'high', 'low', 'close'].some(field => !Number.isFinite(bar[field]) || bar[field] <= 0)) return false
  return bar.low <= Math.min(bar.open, bar.close) && bar.high >= Math.max(bar.open, bar.close)
}

export function orderedDailyBars(bars) {
  return [...new Map((bars || []).filter(validDailyBar).map(bar => [String(bar.time), { ...bar, time: String(bar.time) }])).values()]
    .sort((a, b) => a.time.localeCompare(b.time))
}

export function applyDailyBar(bars, bar, completed = false) {
  if (!validDailyBar(bar)) return bars
  const time = String(bar.time)
  const last = bars.at(-1)?.time
  if (last && time < last && !completed) return bars
  const index = bars.findIndex(item => item.time === time)
  if (last && time < last && index < 0) return bars
  if (index >= 0) return bars.map((item, i) => i === index ? { ...bar, time } : item)
  return [...bars, { ...bar, time }]
}

// Establish/replace a dataset only when its historical prefix changes.
// Normal ticks replace the latest identity or append, preserving the viewport.
export function updateChartSeries(series, previous, next) {
  const historicalChange = next.length < previous.length || previous.slice(0, -1).some((point, index) => JSON.stringify(point) !== JSON.stringify(next[index]))
  if (!previous.length || historicalChange || (previous.length && next[previous.length - 1]?.time !== previous.at(-1).time)) {
    series.setData(next)
    return 'replace'
  }
  for (let index = Math.max(0, previous.length - 1); index < next.length; index += 1) {
    if (JSON.stringify(previous[index]) !== JSON.stringify(next[index])) series.update(next[index])
  }
  return 'update'
}

// A connected stream does not make old daily observations current.
export function dailyFeedStatus(connection, bars, now = new Date()) {
  if (connection !== 'live') return connection
  let expected = getEtMarketDateKey(now)
  if (!(isUsEquityTradingDay(now) && (getMarketSession(now) === 'regular' || millisecondsUntilDailyClose(now) === 0))) {
    const candidate = new Date(`${expected}T16:00:00Z`)
    do { candidate.setUTCDate(candidate.getUTCDate() - 1) } while (!isUsEquityTradingDay(candidate))
    expected = getEtMarketDateKey(candidate)
  }
  return bars.at(-1)?.time >= expected.replaceAll('-', '') ? 'live' : 'stale'
}
